<template>
  <!-- Homepage v2 — admin-only preview (see useLandingVersion). Every section opens
       with a hook, then one plain sentence of benefit. Numbers shown here come only
       from /stats/public_stats/; a stat with no real value is hidden, never padded. -->
  <div class="bg-background text-foreground">

    <!-- Hero -->
    <section class="relative overflow-hidden">
      <div class="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background pointer-events-none"></div>
      <div class="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[500px] rounded-full bg-primary/5 blur-3xl pointer-events-none"></div>

      <div class="relative max-w-3xl mx-auto px-6 pt-16 pb-10 sm:pt-24 sm:pb-12 text-center">
        <div class="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-semibold px-3.5 py-1.5 rounded-full mb-6 border border-primary/20">
          <span class="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>
          Built for Filipino Remote Workers
        </div>

        <h1 class="text-4xl sm:text-5xl lg:text-[3.3rem] font-extrabold text-foreground leading-[1.08] tracking-tight mb-6">
          Get hired faster.{{ ' ' }}<br class="hidden sm:block" />
          <span class="text-primary">Stop refreshing {{ platforms.length }} job boards.</span>
        </h1>

        <p class="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-xl mx-auto">
          Applypa finds remote jobs for you, alerts you the moment one matches, writes the cover letter, and can even apply for you.
        </p>

        <div class="flex flex-wrap gap-3 justify-center mb-5">
          <router-link
            :to="isLoggedIn ? '/job-filters' : '/register'"
            class="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-6 py-3 text-base font-semibold hover:bg-primary/90 transition-colors shadow-sm"
          >
            {{ isLoggedIn ? 'Browse Jobs →' : 'Start Free 14-Day Trial →' }}
          </router-link>
          <a
            href="#v2HowItWorks"
            class="inline-flex items-center gap-2 bg-background text-foreground border border-border rounded-lg px-6 py-3 text-base font-semibold hover:bg-muted/50 transition-colors"
          >
            See How It Works
          </a>
        </div>

        <div class="flex flex-wrap gap-x-5 gap-y-1.5 justify-center text-sm text-muted-foreground mb-6">
          <span class="inline-flex items-center gap-1.5"><i class="fas fa-check text-primary text-xs"></i> No credit card required</span>
          <span class="inline-flex items-center gap-1.5"><i class="fas fa-check text-primary text-xs"></i> Full Premium for 14 days</span>
          <span class="inline-flex items-center gap-1.5"><i class="fas fa-check text-primary text-xs"></i> Logins stored encrypted</span>
        </div>

        <p v-if="stats.active_members >= MIN_MEMBERS_TO_SHOW" class="text-sm text-muted-foreground">
          Join <span class="font-bold text-foreground">{{ fmt(stats.active_members) }}</span> Filipino freelancers already using Applypa.
        </p>
      </div>

      <div class="relative max-w-3xl mx-auto px-6 pb-10">
        <div class="pt-8 border-t border-border">
          <div class="text-xs font-semibold text-muted-foreground text-center uppercase tracking-widest mb-4">One feed from</div>
          <div class="flex flex-wrap justify-center gap-2">
            <span v-for="p in platforms" :key="p" class="bg-muted text-foreground/80 text-sm font-semibold px-3.5 py-1.5 rounded-full border border-border">{{ p }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- How it works — the whole flow in four steps -->
    <section id="v2HowItWorks" class="py-16 sm:py-20 px-6 bg-card scroll-mt-16">
      <div class="max-w-5xl mx-auto">
        <div class="text-sm font-bold text-primary text-center uppercase tracking-widest mb-2">How it works</div>
        <h2 class="text-3xl sm:text-4xl font-extrabold text-foreground text-center mb-3 tracking-tight">From job posted to application sent.</h2>
        <p class="text-base text-muted-foreground text-center mb-12 max-w-lg mx-auto">Set your filters once. Applypa handles the repetitive parts every day after.</p>

        <ol class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <li v-for="(step, i) in steps" :key="step.title" class="relative rounded-2xl border border-border bg-background p-5">
            <div class="flex items-center gap-3 mb-3">
              <span class="w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center shrink-0">{{ i + 1 }}</span>
              <i :class="step.icon" class="text-primary"></i>
            </div>
            <p class="text-base font-bold text-foreground mb-1">{{ step.title }}</p>
            <p class="text-sm text-muted-foreground leading-relaxed">{{ step.body }}</p>
          </li>
        </ol>
      </div>
    </section>

    <!-- Live stats — real counts only -->
    <section v-if="liveStats.length" class="bg-background border-y border-border">
      <div class="max-w-5xl mx-auto px-6 py-12">
        <div class="text-xs font-bold text-primary text-center uppercase tracking-widest mb-6 inline-flex items-center justify-center gap-2 w-full">
          <span class="w-1.5 h-1.5 rounded-full bg-green-500 inline-block animate-pulse"></span> Live on Applypa
        </div>
        <div class="flex flex-wrap justify-center gap-y-8">
          <div v-for="s in liveStats" :key="s.label" class="text-center px-2 basis-1/2 lg:basis-1/4">
            <div class="text-3xl sm:text-4xl font-extrabold text-foreground tracking-tight">{{ fmt(s.value) }}</div>
            <div class="text-sm text-muted-foreground mt-2">{{ s.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Find -->
    <section id="v2Find" class="py-16 sm:py-24 px-6 bg-card scroll-mt-16">
      <div class="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-10 md:gap-14">
        <div class="w-full md:w-1/2">
          <!-- White frame: the graphic itself has a white background -->
          <div class="relative rounded-3xl border border-border bg-white p-3 sm:p-5 overflow-hidden shadow-xl">
            <img :src="jobFiltersImg" alt="Applypa job feed with filters" class="relative w-full h-auto drop-shadow-xl" loading="lazy" />
          </div>
        </div>
        <div class="w-full md:w-1/2">
          <div class="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-bold px-3 py-1.5 rounded-full mb-4"><i class="fas fa-magnifying-glass"></i> Find</div>
          <h2 class="text-2xl sm:text-3xl font-extrabold text-foreground mb-3 leading-tight">{{ platforms.length }} job boards. One feed. No more tab-switching.</h2>
          <p class="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">Every remote job from the Philippines' top platforms lands in one list, already filtered to what you actually want.</p>
          <feature-list :items="findPoints" />
        </div>
      </div>
    </section>

    <!-- Alerts -->
    <section id="v2Alerts" class="py-16 sm:py-24 px-6 bg-background scroll-mt-16">
      <div class="max-w-5xl mx-auto flex flex-col md:flex-row-reverse items-center gap-10 md:gap-14">
        <div class="w-full md:w-1/2 flex justify-center">
          <!-- Illustrative alert — a sample, not a real listing -->
          <div class="w-full max-w-sm rounded-[2rem] border border-border bg-muted/40 p-4 shadow-xl">
            <div class="text-[11px] text-muted-foreground text-center mb-3">Sample alert</div>
            <div class="rounded-2xl bg-card border border-border p-4">
              <div class="flex items-center gap-2 mb-2">
                <span class="w-8 h-8 rounded-full bg-sky-500/15 flex items-center justify-center"><i class="fab fa-telegram text-sky-500"></i></span>
                <div>
                  <p class="text-sm font-bold text-foreground leading-none">Applypa Bot</p>
                  <p class="text-[11px] text-muted-foreground">just now</p>
                </div>
              </div>
              <p class="text-sm text-foreground leading-relaxed">🔔 New job matching <b>"Virtual Assistant"</b></p>
              <p class="text-sm text-foreground font-semibold mt-2">Executive Virtual Assistant</p>
              <p class="text-xs text-muted-foreground">Full-time · Remote</p>
              <div class="mt-3 text-xs font-semibold text-primary">View job & apply →</div>
            </div>
          </div>
        </div>
        <div class="w-full md:w-1/2">
          <div class="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-bold px-3 py-1.5 rounded-full mb-4"><i class="fas fa-bell"></i> Alerts</div>
          <h2 class="text-2xl sm:text-3xl font-extrabold text-foreground mb-3 leading-tight">The first applicants get the replies. Be one of them.</h2>
          <p class="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">Good remote jobs fill up with applicants within hours. Applypa pings you the moment a job matches your filters, even at 3 AM.</p>
          <feature-list :items="alertPoints" />
        </div>
      </div>
    </section>

    <!-- Write -->
    <section id="v2Write" class="py-16 sm:py-24 px-6 bg-card scroll-mt-16">
      <div class="max-w-5xl mx-auto">
        <div class="max-w-2xl mx-auto text-center mb-10">
          <div class="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-bold px-3 py-1.5 rounded-full mb-4"><i class="fas fa-file-alt"></i> Write</div>
          <h2 class="text-2xl sm:text-3xl font-extrabold text-foreground mb-3 leading-tight">A cover letter written for this job, not every job.</h2>
          <p class="text-sm sm:text-base text-muted-foreground leading-relaxed">Clients can spot a copy-pasted letter in one line. Applypa reads the job post and your resume, then writes a letter that answers what this client is asking for.</p>
        </div>

        <!-- Illustrative before/after — sample text, not a real application -->
        <div class="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
          <div class="rounded-2xl border border-border bg-muted/40 p-6">
            <div class="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2"><i class="fas fa-xmark text-red-500"></i> Copy-paste template</div>
            <p class="text-sm text-muted-foreground leading-relaxed italic">"Dear Sir/Madam, I am applying for the position. I am hardworking, a fast learner, and I have good communication skills. I hope you consider my application..."</p>
          </div>
          <div class="rounded-2xl border border-primary/30 bg-primary/5 p-6">
            <div class="text-xs font-bold text-primary uppercase tracking-wider mb-3 flex items-center gap-2"><i class="fas fa-check text-emerald-600"></i> Written by Applypa</div>
            <p class="text-sm text-foreground leading-relaxed italic">"Hi Mark, you mentioned needing someone to manage your inbox and calendar across two time zones. For the past two years I've done exactly that for a US real estate team, keeping 40+ weekly appointments on track..."</p>
          </div>
        </div>
        <p class="text-xs text-muted-foreground text-center mt-3">Sample letters for illustration.</p>

        <div class="max-w-4xl mx-auto mt-10">
          <feature-list :items="writePoints" grid />
        </div>
      </div>
    </section>

    <!-- Auto Apply — fixed dark band on purpose, same reason as the current homepage:
         bg-primary would flip to near-white in dark mode. -->
    <section id="v2AutoApply" class="py-16 sm:py-24 px-6 bg-black text-white scroll-mt-16">
      <div class="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-10 md:gap-14">
        <div class="w-full md:w-1/2">
          <div class="flex flex-wrap items-center gap-2 mb-4">
            <div class="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-bold px-3 py-1.5 rounded-full"><i class="fas fa-bolt"></i> Auto Apply</div>
            <div class="inline-flex items-center gap-1.5 bg-amber-400/20 text-amber-100 text-xs font-bold px-3 py-1.5 rounded-full border border-amber-300/40"><i class="fas fa-link"></i> Works with your linked job board account</div>
          </div>
          <h2 class="text-3xl sm:text-4xl font-extrabold mb-3 leading-tight tracking-tight">Applications go out while you sleep.</h2>
          <p class="text-sm sm:text-base leading-relaxed mb-8 text-white/80">Turn it on once. Applypa finds matching jobs, writes each cover letter, and sends real applications on the days and times you choose.</p>

          <ol class="relative border-l border-white/20 ml-2 space-y-5">
            <li v-for="t in autoApplyTimeline" :key="t.title" class="pl-6 relative">
              <span class="absolute -left-[9px] top-0.5 w-4 h-4 rounded-full bg-white/90 ring-4 ring-black"></span>
              <p class="text-sm font-bold text-white">{{ t.title }}</p>
              <p class="text-sm text-white/60 mt-0.5">{{ t.body }}</p>
            </li>
          </ol>
        </div>
        <div class="w-full md:w-1/2">
          <div class="relative rounded-3xl border border-white/15 bg-white p-3 sm:p-5 overflow-hidden shadow-xl">
            <img :src="automationApplyImg" alt="Auto Apply queue" class="relative w-full h-auto drop-shadow-xl" loading="lazy" />
          </div>
        </div>
      </div>
    </section>

    <!-- Old way vs Applypa — task by task -->
    <section class="py-16 sm:py-24 px-6 bg-card">
      <div class="text-sm font-bold text-primary text-center uppercase tracking-widest mb-2">The Difference</div>
      <h2 class="text-3xl sm:text-4xl font-extrabold text-foreground text-center mb-3 tracking-tight">Same job hunt. Half the busywork.</h2>
      <p class="text-base text-muted-foreground text-center mb-10 max-w-lg mx-auto">Here's what changes, task by task.</p>

      <div class="max-w-3xl mx-auto rounded-2xl border border-border overflow-hidden bg-background">
        <div class="grid grid-cols-[1fr_1fr] sm:grid-cols-[0.8fr_1fr_1fr] text-xs font-bold uppercase tracking-wider bg-muted/60">
          <div class="hidden sm:block px-4 py-3 text-muted-foreground">Task</div>
          <div class="px-4 py-3 text-muted-foreground">The old way</div>
          <div class="px-4 py-3 text-primary">With Applypa</div>
        </div>
        <div v-for="row in comparison" :key="row.task" class="grid grid-cols-[1fr_1fr] sm:grid-cols-[0.8fr_1fr_1fr] border-t border-border text-sm">
          <div class="col-span-2 sm:col-span-1 px-4 pt-3 sm:py-3 font-semibold text-foreground">{{ row.task }}</div>
          <div class="px-4 py-3 text-muted-foreground flex gap-2"><i class="fas fa-xmark text-red-500 mt-1 text-xs"></i>{{ row.old }}</div>
          <div class="px-4 py-3 text-foreground flex gap-2"><i class="fas fa-check text-emerald-600 mt-1 text-xs"></i>{{ row.now }}</div>
        </div>
      </div>
    </section>

    <!-- Testimonials -->
    <section class="py-16 sm:py-24 px-6 bg-background">
      <div class="text-sm font-bold text-primary text-center uppercase tracking-widest mb-2">Real Users</div>
      <h2 class="text-3xl sm:text-4xl font-extrabold text-foreground text-center mb-3 tracking-tight">Filipino freelancers, in their own words.</h2>
      <p class="text-base text-muted-foreground text-center mb-12 max-w-sm mx-auto">Unedited, from emails, comments, and messages.</p>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
        <div v-for="(t, index) in testimonials" :key="index" class="bg-card rounded-2xl p-6 border border-border shadow-sm flex flex-col">
          <div class="flex gap-1 mb-3">
            <i class="fas fa-star text-yellow-400 text-sm" v-for="n in 5" :key="'v2t' + index + n"></i>
          </div>
          <p class="text-sm text-foreground leading-relaxed flex-1 mb-4">"{{ t.quote }}"
            <span v-if="t.translation" class="block mt-1 text-xs text-muted-foreground italic">"{{ t.translation }}"</span>
          </p>
          <div class="text-xs font-semibold text-muted-foreground">— {{ t.name }}</div>
        </div>
      </div>
    </section>

    <!-- Trust strip -->
    <section class="py-12 px-6 bg-muted border-y border-border">
      <div class="max-w-4xl mx-auto grid sm:grid-cols-3 gap-6 text-center">
        <div v-for="item in trust" :key="item.title" class="flex flex-col items-center gap-2">
          <i :class="item.icon" class="text-primary text-xl"></i>
          <p class="text-sm font-semibold text-foreground">{{ item.title }}</p>
          <p class="text-xs text-muted-foreground max-w-[220px]">{{ item.body }}</p>
        </div>
      </div>
    </section>

    <!-- Pricing -->
    <section class="py-16 sm:py-24 px-6 bg-card">
      <div class="text-sm font-bold text-primary text-center uppercase tracking-widest mb-2">Pricing</div>
      <h2 class="text-3xl sm:text-4xl font-extrabold text-foreground text-center mb-3 tracking-tight">Try everything free for 14 days.</h2>
      <p class="text-base text-muted-foreground text-center mb-12 max-w-lg mx-auto">Full Premium access, no credit card. When the trial ends you stay on the Free plan, or upgrade.</p>

      <div class="max-w-4xl mx-auto grid sm:grid-cols-2 gap-4">
        <div class="rounded-2xl border border-border bg-background p-6">
          <div class="text-sm font-bold text-muted-foreground uppercase tracking-wider mb-1">Basic</div>
          <div class="text-3xl font-extrabold text-foreground mb-4">{{ formatPeso(basicFeatures.price) }}<span class="text-sm font-medium text-muted-foreground">/mo</span></div>
          <ul class="space-y-2 text-sm text-muted-foreground">
            <li v-for="bullet in basicBullets" :key="bullet" class="flex gap-2"><i class="fas fa-check text-primary text-xs mt-1"></i>{{ bullet }}</li>
          </ul>
        </div>
        <div class="rounded-2xl border-2 border-primary bg-background p-6 relative">
          <div class="absolute -top-3 right-6 bg-primary text-primary-foreground text-[11px] font-bold px-2.5 py-1 rounded-full">What the trial includes</div>
          <div class="text-sm font-bold text-primary uppercase tracking-wider mb-1">Premium</div>
          <div class="text-3xl font-extrabold text-foreground mb-4">{{ formatPeso(premiumFeatures.price) }}<span class="text-sm font-medium text-muted-foreground">/mo</span></div>
          <ul class="space-y-2 text-sm text-muted-foreground">
            <li v-for="bullet in premiumBullets" :key="bullet" class="flex gap-2"><i class="fas fa-check text-primary text-xs mt-1"></i>{{ bullet }}</li>
          </ul>
        </div>
      </div>
      <div class="text-center mt-8">
        <router-link to="/subscription/plans" class="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-3 px-8 rounded-lg inline-block transition">
          Compare All Plans
        </router-link>
      </div>
    </section>

    <!-- FAQ -->
    <section class="py-16 sm:py-24 px-6 bg-muted">
      <div class="max-w-2xl mx-auto">
        <div class="text-sm font-bold text-primary text-center uppercase tracking-widest mb-2">FAQ</div>
        <h2 class="text-3xl sm:text-4xl font-extrabold text-foreground text-center mb-3 tracking-tight">Questions we hear the most</h2>
        <p class="text-base text-muted-foreground text-center mb-10 max-w-sm mx-auto">Straight answers before you sign up.</p>

        <div class="space-y-3">
          <div v-for="(faq, index) in faqs" :key="index" class="bg-card rounded-xl border border-border overflow-hidden">
            <button
              @click="toggleFaq(index)"
              :aria-expanded="openFaq === index"
              class="w-full flex items-center justify-between px-5 py-4 text-left focus:outline-none group"
            >
              <span class="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">{{ faq.question }}</span>
              <i class="fas fa-chevron-down text-muted-foreground text-xs transition-transform duration-200 ml-4 shrink-0" :class="{ 'rotate-180': openFaq === index }"></i>
            </button>
            <div v-show="openFaq === index" class="px-5 pb-5">
              <p class="text-sm text-muted-foreground leading-relaxed">{{ faq.answer }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Final CTA — fixed dark band, same reason as Auto Apply -->
    <section class="py-16 sm:py-24 px-6 bg-black text-white text-center">
      <h2 class="text-3xl sm:text-4xl font-extrabold mb-3 tracking-tight">
        <template v-if="stats.jobs_last_7_days">{{ fmt(stats.jobs_last_7_days) }} new remote jobs this week.</template>
        <template v-else>Your next remote job is being posted right now.</template>
      </h2>
      <p class="text-base mb-4 max-w-md mx-auto text-white/85">
        Be first in line for the ones that fit you. Set up your filters and alerts in a few minutes.
      </p>
      <p class="text-sm mb-8 text-white/60">14-day free trial · No credit card required</p>
      <div v-if="!isLoggedIn" class="flex flex-col items-center gap-3">
        <div class="flex flex-wrap gap-3 justify-center">
          <router-link to="/register" class="bg-white text-black rounded-xl px-7 py-3.5 text-base font-bold hover:opacity-90 transition">
            Start Your Free Trial →
          </router-link>
          <a href="https://calendly.com/applypa-support/30min" target="_blank" rel="noopener" class="border-2 border-white/40 text-white rounded-xl px-7 py-3.5 text-base font-bold hover:bg-white/10 transition">
            Book a Demo
          </a>
        </div>
        <router-link to="/login" class="text-sm text-white/70 hover:text-white underline underline-offset-4">Already have an account? Log in</router-link>
      </div>
      <div v-else>
        <router-link to="/job-filters" class="bg-white text-black rounded-xl px-7 py-3.5 text-sm font-bold hover:opacity-90 transition inline-block">
          Browse Jobs →
        </router-link>
      </div>
    </section>

  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onBeforeUnmount, h } from 'vue';
import authStore from '@/store/auth';
import { useGuestPreview } from '@/composables/useLandingVersion';
import apiClient from '@/services/api';
import {
  resolvePlanFeatures,
  formatPeso,
  coverLettersBullet,
  autoApplyBullet,
  directApplyBullet,
  useSubscriptionPlans,
} from '@/composables/useSubscriptionPlans';
// Newer hero graphics made for the OLJ homepage. Note: the Auto Apply one shows
// "Connected to OnlineJobs.ph" and an illustrative 18% reply rate — revisit before v2 goes public.
import jobFiltersImg from '@/assets/applypa/job-filters-hero-onlinejobs.png';
import automationApplyImg from '@/assets/applypa/auto-apply-hero-onlinejobs.png';

// Icon + title + one-line body, used by every feature section.
const FeatureList = {
  props: { items: { type: Array, required: true }, grid: Boolean },
  setup(props) {
    return () => h(
      'div',
      { class: props.grid ? 'grid sm:grid-cols-3 gap-5' : 'space-y-4' },
      props.items.map((item) => h('div', { key: item.title, class: 'flex items-start gap-3' }, [
        h('div', { class: 'w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0' }, [
          h('i', { class: `${item.icon} text-primary text-sm` }),
        ]),
        h('div', [
          h('p', { class: 'text-sm font-bold text-foreground' }, item.title),
          h('p', { class: 'text-sm text-muted-foreground mt-0.5' }, item.body),
        ]),
      ])),
    );
  },
};

export default {
  name: 'HomePageV2',
  components: { FeatureList },
  setup() {
    // Admin preview (/?preview=1) shows the guest CTAs visitors see.
    const { isGuestPreview } = useGuestPreview();
    const isLoggedIn = computed(() => authStore.state.isLoggedIn && !isGuestPreview.value);

    // Sources users can see in Job Filters (DEFAULT_SOURCES in JobFiltersPage.vue). OLJ is
    // hidden from the feed and only referred to as the "linked job board"; PhilJobNet has a
    // scraper but isn't in the feed yet. Counts in the copy are derived from this list.
    // BossJob dropped 2026-09-25: it refuses every address we can reach, so no
    // jobs are coming from it and naming it here would promise something the
    // feed does not deliver. Put it back when a working route exists.
    const platforms = ['JobStreet', 'Indeed', 'Kalibrr', 'Cloudstaff', 'Emapta', 'VAWorkers PH', 'FindTalent.ph', 'HireTalent.ph', 'OFM Jobs'];
    const platformList = `${platforms.slice(0, -1).join(', ')}, and ${platforms[platforms.length - 1]}`;

    const steps = [
      { icon: 'fas fa-sliders', title: 'Set your filters', body: 'Pick the job types, keywords, and salary you want. Save as many searches as you need.' },
      { icon: 'fas fa-bell', title: 'Get alerted first', body: 'The moment a matching job posts, you hear about it on Telegram, Discord, or email.' },
      { icon: 'fas fa-wand-magic-sparkles', title: 'Apply with AI', body: 'Generate a cover letter tailored to the job and your resume in seconds.' },
      { icon: 'fas fa-bolt', title: 'Or let it auto-apply', body: 'Turn on Auto Apply and applications go out on your schedule.' },
    ];

    const findPoints = [
      { icon: 'fas fa-layer-group', title: 'Every platform in one list', body: `${platformList}, side by side.` },
      { icon: 'fas fa-filter', title: 'Only jobs worth your time', body: 'Filter by keyword, job type, salary, and category before anything reaches you.' },
      { icon: 'fas fa-bookmark', title: 'Saved searches, one click away', body: 'Keep a filter for each kind of role and switch between them instantly.' },
    ];

    const alertPoints = [
      { icon: 'fab fa-telegram', title: 'Telegram, Discord & email', body: 'Get alerts wherever you already are.' },
      { icon: 'fas fa-bullseye', title: 'Only your matches', body: 'Alerts follow your saved filters, so there is no spam.' },
      { icon: 'fas fa-moon', title: 'Watches while you sleep', body: 'Applypa checks job boards around the clock.' },
    ];

    const writePoints = [
      { icon: 'fas fa-wand-magic-sparkles', title: 'One click', body: 'Hit "Generate with AI" on a job post and your letter is ready in seconds.' },
      { icon: 'fas fa-user-pen', title: 'Built from your resume', body: 'Uses your real experience, not generic filler.' },
      { icon: 'fas fa-pen-to-square', title: 'Yours to edit', body: 'Review and tweak before you send a manual application.' },
    ];

    // Mirrors the real Auto Apply flow: match → letter → queued (skippable) → sent on schedule.
    const autoApplyTimeline = [
      { title: 'A matching job is posted', body: 'Auto Apply checks new listings against your filters.' },
      { title: 'Your cover letter is written', body: 'A tailored AI letter for that specific job.' },
      { title: 'It waits in your queue', body: 'See every application before its send time. Skip any you don\'t want.' },
      { title: 'It\'s sent on your schedule', body: 'Only on the days and times you picked. Turn it off any time.' },
    ];

    const comparison = [
      { task: 'Finding jobs', old: `Open ${platforms.length} websites`, now: 'One feed' },
      { task: 'Catching new posts', old: 'Refresh all day', now: 'Instant alerts' },
      { task: 'Cover letters', old: 'Copy-paste the same one', now: 'Tailored in seconds' },
      { task: 'Overnight jobs', old: 'Gone by morning', now: 'Alerted, or auto-applied' },
      { task: 'Applying', old: 'One at a time, by hand', now: 'On autopilot' },
    ];

    // Real user testimonials — same set as the current homepage. Do not fabricate.
    const testimonials = [
      { quote: 'Maganda app na yan. Automatic nagfofofollow up message after mo mag apply. Kaya may client na nagrereply talaga.', translation: "It's a good app. It automatically sends follow-up messages after you apply — that's why clients actually reply.", name: 'Samiya L.' },
      { quote: 'Im using Applypa, mabilis talaga to filter yung gusto mong job posting.', translation: "It's really fast to filter the job postings you want.", name: 'Kang X.' },
      { quote: "I've already tried it — mas mabilis sya mkpg apply with free generate cover letter and smooth sya gmitin.", translation: "It's faster to apply with the free cover letter generator and smooth to use.", name: 'Anjeanette D.' },
      { quote: "Thank you so much!! I'm so excited to use your platform. Kudos for this awesome innovation!! I use it almost every day. User interface is neat. And features are awesome.", translation: null, name: 'Joemae P.' },
      { quote: "Anything is good and perfectly well working. I'm so amazed about the structure of this platform. Great job!", translation: null, name: 'Aleman J.' },
      { quote: "Ma'am it works. Oh my goodness.", translation: null, name: 'JR E.' },
    ];

    const trust = [
      { icon: 'fas fa-lock', title: 'Encrypted logins', body: 'Your platform credentials are encrypted and never shared with third parties.' },
      { icon: 'fas fa-hand-holding-dollar', title: 'Free for 14 days', body: 'Full Premium access with no credit card required.' },
      { icon: 'fas fa-users', title: 'Built with Filipino freelancers', body: 'Shaped by feedback from the people who use it every day.' },
    ];

    const { fetchSubscriptionPlans, findPlan } = useSubscriptionPlans();
    const basicFeatures = computed(() => resolvePlanFeatures(findPlan('basic', 30), 'basic'));
    const premiumFeatures = computed(() => resolvePlanFeatures(findPlan('premium', 30), 'premium'));
    const basicBullets = computed(() => [
      coverLettersBullet(basicFeatures.value),
      autoApplyBullet(basicFeatures.value),
      directApplyBullet(basicFeatures.value),
    ].filter(Boolean));
    const premiumBullets = computed(() => [
      coverLettersBullet(premiumFeatures.value),
      autoApplyBullet(premiumFeatures.value),
      premiumFeatures.value.has_priority_support ? 'Priority support' : directApplyBullet(premiumFeatures.value),
    ].filter(Boolean));

    // Same accuracy rules as the current FAQ: Auto Apply needs a linked job board account,
    // runs on schedule with no per-application approval; user controls are skip and off.
    const faqs = [
      {
        question: 'Is Applypa free?',
        answer: 'You get a 14-day free trial with full Premium access, no credit card required. After that you move to the Free plan automatically, and you can upgrade to Basic or Premium any time for higher limits.',
      },
      {
        question: 'Should I apply manually or use Auto Apply?',
        answer: 'Both work. Apply manually when you want to review and edit every cover letter yourself. Turn on Auto Apply when you want applications going out on a schedule without doing each one by hand. Many users do both.',
      },
      {
        question: 'How does Auto Apply work?',
        answer: 'Auto Apply requires a linked job board account. Once it\'s on, Applypa finds listings that match your filters, writes a tailored AI cover letter, and submits the application on the days and times you set. There\'s no per-application approval step while it\'s running.',
      },
      {
        question: 'Can I stop an application before it\'s sent?',
        answer: 'Yes. Every queued Auto Apply application shows up before its scheduled send time, and you can skip it. You can also turn Auto Apply off completely at any time.',
      },
      {
        question: 'Is my job board login safe?',
        answer: 'Your credentials are encrypted before they are stored, used only to fetch listings or submit applications on your behalf, and never shared with third parties.',
      },
      {
        question: 'Will employers know I used AI?',
        answer: 'Letters are written from your resume and the specific job post, not a generic template. For manual applications you can review and edit before sending. Auto Apply sends its letter without a manual edit step, which is the tradeoff for running on autopilot.',
      },
      {
        question: 'Which job platforms are supported?',
        answer: `Applypa brings listings from ${platformList} into one feed. Auto Apply currently works only with a linked job board account; the others are for browsing, filtering, and alerts. LinkedIn and Upwork support is coming soon.`,
      },
      {
        question: 'What kinds of jobs can I find?',
        answer: 'Remote and freelance roles from our supported platforms: virtual assistant, customer support, bookkeeping, social media, data entry, web development, graphic design, and more.',
      },
    ];
    const openFaq = ref(null);
    const toggleFaq = (index) => {
      openFaq.value = openFaq.value === index ? null : index;
    };

    // Real counts only — no baselines. A stat that's missing or zero is hidden, and the
    // "Join N freelancers" line waits until N is big enough to be social proof.
    const MIN_MEMBERS_TO_SHOW = 100;
    const stats = reactive({
      active_members: null,
      jobs_processed: null,
      cover_letters: null,
      jobs_last_7_days: null,
      auto_applications_sent: null,
    });
    const liveStats = computed(() => [
      { label: 'New jobs in the last 7 days', value: stats.jobs_last_7_days },
      { label: 'Jobs indexed', value: stats.jobs_processed },
      { label: 'AI cover letters written', value: stats.cover_letters },
      { label: 'Auto Apply applications sent', value: stats.auto_applications_sent },
    ].filter((s) => s.value));
    const fmt = (n) => Number(n).toLocaleString('en-US');

    let statsInterval = null;
    const fetchStats = async () => {
      try {
        const res = await apiClient.get('/stats/public_stats/');
        Object.keys(stats).forEach((k) => {
          if (res.data[k] != null) stats[k] = res.data[k];
        });
      } catch {
        // keep previous values on error
      }
    };

    onMounted(() => {
      fetchStats();
      fetchSubscriptionPlans();
      statsInterval = setInterval(fetchStats, 60000);
    });
    onBeforeUnmount(() => clearInterval(statsInterval));

    return {
      isLoggedIn,
      platforms,
      steps,
      findPoints,
      alertPoints,
      writePoints,
      autoApplyTimeline,
      comparison,
      testimonials,
      trust,
      basicFeatures,
      premiumFeatures,
      basicBullets,
      premiumBullets,
      formatPeso,
      faqs,
      openFaq,
      toggleFaq,
      stats,
      MIN_MEMBERS_TO_SHOW,
      liveStats,
      fmt,
      jobFiltersImg,
      automationApplyImg,
    };
  },
};
</script>
